/**
 * GanttChart
 *
 * A component for visualizing project timelines and task schedules
 *
 * Primary responsibilities:
 * - Display tasks with their start and end dates
 * - Support task dependencies and milestones
 * - Allow for collapsible task groups
 * - Provide visual indication of task progress and status
 */

import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import {
  IconCalendar,
  IconChevronDown,
  IconChevronRight,
  IconCheckbox,
  IconFlag,
  IconArrowRight
} from '@tabler/icons-react'

export interface Task {
  id: string
  name: string
  start: string // ISO date string
  end: string // ISO date string
  progress?: number // 0-100
  dependencies?: string[] // IDs of tasks this depends on
  milestone?: boolean
  group?: boolean
  collapsed?: boolean
  subtasks?: Task[]
  color?: string
  status?: 'not-started' | 'in-progress' | 'completed' | 'delayed' | string
  owner?: string
  [key: string]: any // Allow for additional custom properties
}

interface GanttChartProps {
  title?: string
  tasks: Task[]
  className?: string
  startDate?: string // Override to set chart start date
  endDate?: string // Override to set chart end date
  showDependencies?: boolean
  showProgress?: boolean
  compact?: boolean
}

export const GanttChart: React.FC<GanttChartProps> = ({
  title = 'Project Timeline',
  tasks,
  className,
  startDate: propStartDate,
  endDate: propEndDate,
  showDependencies = true,
  showProgress = true,
  compact = false
}) => {
  // State for collapsed task groups
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    tasks.forEach((task) => {
      if (task.group && task.collapsed) {
        initial[task.id] = true
      }
    })
    return initial
  })

  // Calculate chart date range
  const calculateDateRange = () => {
    if (propStartDate && propEndDate) {
      return {
        startDate: new Date(propStartDate),
        endDate: new Date(propEndDate)
      }
    }

    let minDate = new Date()
    let maxDate = new Date()

    // Find min and max dates from tasks
    tasks.forEach((task) => {
      const start = new Date(task.start)
      const end = new Date(task.end)

      if (start < minDate) minDate = start
      if (end > maxDate) maxDate = end

      // Check subtasks if any
      if (task.subtasks) {
        task.subtasks.forEach((subtask) => {
          const subStart = new Date(subtask.start)
          const subEnd = new Date(subtask.end)

          if (subStart < minDate) minDate = subStart
          if (subEnd > maxDate) maxDate = subEnd
        })
      }
    })

    // Add some padding
    minDate.setDate(minDate.getDate() - 2)
    maxDate.setDate(maxDate.getDate() + 2)

    return { startDate: minDate, endDate: maxDate }
  }

  const { startDate, endDate } = calculateDateRange()

  // Calculate total project duration in days
  const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: startDate.getFullYear() !== date.getFullYear() ? 'numeric' : undefined
    })
  }

  // Calculate bar position and width
  const calculateBarStyles = (task: Task) => {
    const taskStart = new Date(task.start)
    const taskEnd = new Date(task.end)

    // Calculate days from project start
    const startOffset = Math.max(
      0,
      Math.ceil((taskStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    )
    const taskDuration = Math.max(
      1,
      Math.ceil((taskEnd.getTime() - taskStart.getTime()) / (1000 * 60 * 60 * 24))
    )

    // Convert to percentages
    const left = (startOffset / totalDays) * 100
    const width = (taskDuration / totalDays) * 100

    return {
      left: `${left}%`,
      width: `${width}%`
    }
  }

  // Get color class based on task status or custom color
  const getTaskColorClass = (task: Task) => {
    if (task.color) return task.color

    if (task.milestone) {
      return 'bg-purple-500 dark:bg-purple-600'
    }

    switch (task.status) {
      case 'completed':
        return 'bg-green-500 dark:bg-green-600'
      case 'in-progress':
        return 'bg-blue-500 dark:bg-blue-600'
      case 'delayed':
        return 'bg-red-500 dark:bg-red-600'
      case 'not-started':
      default:
        return 'bg-gray-400 dark:bg-gray-600'
    }
  }

  // Toggle group collapse state
  const toggleGroup = (taskId: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [taskId]: !prev[taskId]
    }))
  }

  // Task row component
  const renderTask = (task: Task, level: number = 0) => {
    const isGroup = task.group || (task.subtasks && task.subtasks.length > 0)
    const isCollapsed = collapsedGroups[task.id]
    const leftPadding = level * 20

    return (
      <React.Fragment key={task.id}>
        <div className="border-border flex items-start border-b py-2">
          <div className="flex flex-1 items-center" style={{ paddingLeft: `${leftPadding}px` }}>
            {isGroup && (
              <button
                className="mr-1 rounded p-0.5 hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => toggleGroup(task.id)}
              >
                {isCollapsed ? <IconChevronRight size={14} /> : <IconChevronDown size={14} />}
              </button>
            )}

            <div className="flex items-center">
              {task.milestone ? (
                <IconFlag size={14} className="mr-1.5 text-purple-500" />
              ) : (
                <IconCheckbox
                  size={14}
                  className={cn(
                    'mr-1.5',
                    task.status === 'completed' ? 'text-green-500' : 'text-muted-foreground'
                  )}
                />
              )}
              <span
                className={cn(
                  compact ? 'text-xs' : 'text-sm',
                  task.milestone ? 'font-medium' : '',
                  isGroup ? 'font-medium' : ''
                )}
              >
                {task.name}
              </span>
            </div>
          </div>

          <div className="text-muted-foreground ml-4 flex min-w-[140px] items-center text-xs">
            {!compact && (
              <>
                {formatDate(task.start)}
                <IconArrowRight size={12} className="mx-1" />
              </>
            )}
            {formatDate(task.end)}
          </div>
        </div>

        <div className={cn('border-border relative h-8 border-b', compact ? 'h-6' : '')}>
          {/* Task bar */}
          <div
            className={cn(
              'absolute top-1/2 h-5 -translate-y-1/2 rounded-sm',
              getTaskColorClass(task),
              compact ? 'h-4' : '',
              task.milestone ? 'size-[14px] rotate-45 rounded-sm' : ''
            )}
            style={
              task.milestone
                ? { left: calculateBarStyles(task).left, marginLeft: '-7px' }
                : calculateBarStyles(task)
            }
          >
            {/* Progress bar */}
            {!task.milestone && showProgress && typeof task.progress === 'number' && (
              <div
                className="absolute inset-y-0 left-0 rounded-sm bg-black/10 dark:bg-white/10"
                style={{ width: `${task.progress}%` }}
              />
            )}
          </div>

          {/* Dependencies */}
          {showDependencies &&
            task.dependencies?.length &&
            task.dependencies.map((depId) => {
              const depTask = tasks.find((t) => t.id === depId)
              if (!depTask) return null

              // Simple straight line for now - in a full implementation, you'd want to calculate
              // the proper path between tasks
              return (
                <div
                  key={`${task.id}-${depId}`}
                  className="absolute border-t border-gray-300 dark:border-gray-600"
                  style={{
                    left: calculateBarStyles(depTask).left,
                    width: `calc(${calculateBarStyles(task).left} - ${calculateBarStyles(depTask).left})`,
                    top: '-1px'
                  }}
                />
              )
            })}
        </div>

        {/* Render subtasks if expanded */}
        {isGroup &&
          !isCollapsed &&
          task.subtasks &&
          task.subtasks.map((subtask) => renderTask(subtask, level + 1))}
      </React.Fragment>
    )
  }

  // Generate month headers
  const renderTimeHeader = () => {
    const months: Record<string, boolean> = {}
    let currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      const monthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`
      months[monthKey] = true
      currentDate.setMonth(currentDate.getMonth() + 1)
    }

    return (
      <div className="border-border flex border-b">
        <div className="flex-1"></div>
        <div className="min-w-[140px]"></div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="border-b p-3">
        <div className="flex items-center gap-2">
          <IconCalendar size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
        </div>
      </div>

      <div className="overflow-x-auto">
        {renderTimeHeader()}

        <div className="relative">{tasks.map((task) => renderTask(task))}</div>
      </div>
    </div>
  )
}
